import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, Copy, Lightbulb, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg, domaineLabel, URGENCES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import VoiceRecorder from "@/components/VoiceRecorder";
import MediaUpload from "@/components/MediaUpload";
import PhotoUpload from "@/components/PhotoUpload";
import StatutHoraires from "@/components/StatutHoraires";

export default function TicketWizard() {
  const { domaine } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const contact = user?.contact;
  const qrSite = params.get("site");
  const qrMateriel = params.get("materiel");
  const qrMaterielId = params.get("materiel_id");

  const [tree, setTree] = useState(null);
  const [nodeId, setNodeId] = useState(null);
  const [path, setPath] = useState([]);
  const [solution, setSolution] = useState(null);
  const [solutionImage, setSolutionImage] = useState(null);
  const [phase, setPhase] = useState("qcm"); // qcm | solution | form | done
  const [siteId, setSiteId] = useState(qrSite || contact?.sites?.[0]?.id || null);
  const [urgence, setUrgence] = useState("genant");
  const [description, setDescription] = useState("");
  const [transcription, setTranscription] = useState("");
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [marque, setMarque] = useState("");
  const [serie, setSerie] = useState("");
  const [rgpd, setRgpd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    api.get(`/trees/published/${domaine}`).then(({ data }) => {
      setTree(data);
      if (data) setNodeId(data.root_node_id);
      else setPhase("form");
    });
  }, [domaine]);

  useEffect(() => {
    if (photos.length || description || transcription) {
      localStorage.setItem("bkprod_drafts", JSON.stringify([{ domaine, description, transcription, photos, at: Date.now() }]));
    }
  }, [photos, description, transcription, domaine]);

  const node = tree?.nodes?.find((n) => n.id === nodeId);

  const answer = (a) => {
    const step = { question: node.question, reponse: a.label, node_id: node.id };
    setPath([...path, step]);
    if (a.solution) {
      setSolution(a.solution);
      setSolutionImage(a.solution_image_url || null);
      setPhase("solution");
    } else if (a.next_node_id) {
      setNodeId(a.next_node_id);
    } else {
      setPhase("form");
    }
  };

  const back = () => {
    if (phase === "form" && tree && path.length) {
      setPhase("qcm");
      const last = path[path.length - 1];
      setNodeId(last.node_id);
      setPath(path.slice(0, -1));
      return;
    }
    if (phase === "solution") {
      setPhase("qcm");
      const last = path[path.length - 1];
      setNodeId(last.node_id);
      setPath(path.slice(0, -1));
      setSolution(null);
      return;
    }
    if (path.length > 1) {
      const prev = path[path.length - 1];
      setNodeId(prev.node_id);
      setPath(path.slice(0, -1));
    } else {
      navigate("/");
    }
  };

  const submit = async (autoResolved) => {
    if (!autoResolved && !description.trim() && !transcription.trim() && path.length === 0) {
      return toast.error("Décrivez le problème (texte ou vocal)");
    }
    if (!autoResolved && (photos.length || transcription.trim()) && !rgpd) {
      return toast.error("Consentement RGPD requis pour envoyer des photos/vocal");
    }
    setBusy(true);
    try {
      const site = contact?.sites?.find((s) => s.id === siteId);
      const { data } = await api.post("/tickets", {
        domaine,
        urgence: autoResolved ? "mineur" : urgence,
        site_id: siteId,
        site_nom: site?.nom,
        qcm_path: path,
        description_texte: autoResolved ? "Problème résolu via la solution guidée." : description,
        transcription_vocale: autoResolved ? "" : transcription,
        photos: autoResolved ? [] : photos,
        resolu_automatiquement: autoResolved,
        solution_appliquee: autoResolved ? solution : null,
        consentement_rgpd: rgpd,
        materiel_concerne: qrMateriel || null,
        materiel_id: qrMaterielId || null,
        video_url: autoResolved ? null : videos[0]?.url || null,
        materiel_marque: marque.trim() || null,
        materiel_serie_saisi: serie.trim() || null,
      });
      localStorage.removeItem("bkprod_drafts");
      setCreated(data);
      setPhase("done");
    } catch (e) {
      toast.error(errMsg(e, "Envoi impossible"));
    } finally {
      setBusy(false);
    }
  };

  if (phase === "done" && created) {
    return (
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="ticket-confirmation">
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
          <CheckCircle2 size={28} className="text-emerald-600 mb-3" />
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">
            {created.resolu_automatiquement ? "Problème résolu, ticket clôturé" : "Ticket transmis à BKPRO"}
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            {created.resolu_automatiquement
              ? "Aucune intervention nécessaire. La déclaration est archivée pour votre historique."
              : "Votre technicien reçoit le diagnostic complet dans BKFlow."}
          </p>
          {created.prise_en_charge_label && (
            <div className="mt-4 rounded-lg bg-white border border-emerald-200 px-3 py-2.5" data-testid="prise-en-charge">
              <div className="bk-label mb-1">Prise en charge estimée</div>
              <div className="text-sm font-semibold text-slate-900">{created.prise_en_charge_label}</div>
              {created.prise_en_charge_hors_horaires && (
                <div className="text-xs text-slate-500 mt-1">
                  Déclaré en dehors de nos horaires : le compteur démarre à la réouverture du bureau.
                </div>
              )}
            </div>
          )}
          <div className="mt-5 flex items-center gap-2">
            <span className="font-mono-bk text-lg font-semibold text-slate-900" data-testid="ticket-reference">{created.reference}</span>
            <button onClick={() => navigator.clipboard?.writeText(created.reference)} className="text-slate-400 hover:text-slate-900">
              <Copy size={15} />
            </button>
          </div>
        </div>
        {!created.resolu_automatiquement && <StatutHoraires />}
        <div className="flex gap-3">
          <Button data-testid="goto-suivi-btn" onClick={() => navigate(`/suivi/${created.id}`)} className="h-12 rounded-xl bg-[#005CFF] hover:bg-[#0047CC]">
            Suivre mon ticket
          </Button>
          <Button variant="outline" onClick={() => navigate("/")} className="h-12 rounded-xl">Retour à l'accueil</Button>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="space-y-8" data-testid="ticket-wizard">
      <div className="flex items-center gap-3">
        <button data-testid="wizard-back-btn" onClick={back} className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:text-slate-900">
          <ArrowLeft size={16} />
        </button>
        <div>
          <div className="bk-label">{domaineLabel(domaine)}</div>
          <div className="text-sm text-slate-500">
            {phase === "qcm" ? `Qualification · étape ${path.length + 1}` : phase === "solution" ? "Solution proposée" : "Détails du ticket"}
          </div>
        </div>
      </div>

      {qrMateriel && (
        <div className="rounded-xl border border-[#005CFF]/40 bg-blue-50 px-4 py-3" data-testid="qr-context">
          <div className="bk-label mb-1">Fiche pré-remplie par QR code</div>
          <div className="text-sm text-slate-800">
            {qrMateriel}
            {qrSite && contact?.sites?.find((s) => s.id === qrSite) ? ` · ${contact.sites.find((s) => s.id === qrSite).nom}` : ""}
          </div>
        </div>
      )}

      {path.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="qcm-breadcrumb">
          {path.map((s, i) => (
            <span key={`${s.question}-${s.reponse}-${i}`} className="text-xs rounded-full bg-slate-100 border border-slate-200 px-3 py-1 text-slate-700">
              {s.reponse}
            </span>
          ))}
        </div>
      )}

      <AnimatePresence mode="wait">
        {phase === "qcm" && node && (
          <motion.div key={node.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-4">
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{node.question}</h1>
            {node.image_url && (
              <img
                src={node.image_url.startsWith("http") ? node.image_url : `${process.env.REACT_APP_BACKEND_URL}${node.image_url}`}
                alt={node.question}
                data-testid="qcm-node-image"
                className="w-full max-h-72 object-cover rounded-2xl border border-slate-200"
              />
            )}
            <div className="space-y-3">
              {node.answers.map((a, i) => (
                <button
                  key={`${node.id}-${a.label}-${i}`}
                  data-testid={`qcm-answer-${i}`}
                  onClick={() => answer(a)}
                  className="w-full text-left rounded-xl border border-slate-200 bg-white px-5 py-4 text-base text-slate-800 hover:border-[#005CFF] hover:bg-slate-50 transition-colors focus:ring-2 focus:ring-[#005CFF] focus:outline-none"
                >
                  {a.label}
                </button>
              ))}
            </div>
            <button data-testid="qcm-skip-btn" onClick={() => setPhase("form")} className="text-sm text-slate-500 hover:text-slate-900 underline">
              Je ne sais pas — passer directement au ticket
            </button>
          </motion.div>
        )}

        {phase === "solution" && (
          <motion.div key="solution" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5" data-testid="qcm-solution">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <Lightbulb size={20} className="text-amber-500 mb-3" />
              <div className="bk-label mb-2">Essayez ceci avant intervention</div>
              <p className="text-base leading-relaxed text-slate-800">{solution}</p>
              {solutionImage && (
                <img
                  src={solutionImage.startsWith("http") ? solutionImage : `${process.env.REACT_APP_BACKEND_URL}${solutionImage}`}
                  alt="Illustration de la solution"
                  data-testid="qcm-solution-image"
                  className="w-full max-h-80 object-contain rounded-xl border border-slate-200 bg-white mt-4"
                />
              )}
            </div>
            <div className="space-y-3">
              <Button data-testid="solution-resolved-btn" onClick={() => submit(true)} disabled={busy} className="w-full h-14 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-base">
                {busy ? <Loader2 className="animate-spin" size={18} /> : "C'est résolu, merci"}
              </Button>
              <Button data-testid="solution-not-resolved-btn" variant="outline" onClick={() => setPhase("form")} className="w-full h-14 rounded-xl text-base">
                Non résolu, je déclare le ticket
              </Button>
            </div>
          </motion.div>
        )}

        {phase === "form" && (
          <motion.div key="form" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
            {contact?.sites?.length > 1 && (
              <div className="space-y-2">
                <div className="bk-label">Site concerné</div>
                <div className="grid gap-2">
                  {contact.sites.map((s) => (
                    <button
                      key={s.id}
                      data-testid={`site-option-${s.id}`}
                      onClick={() => setSiteId(s.id)}
                      className={`text-left rounded-xl border px-4 py-3 transition-colors ${siteId === s.id ? "border-[#005CFF] bg-blue-50" : "border-slate-200 hover:border-slate-400"}`}
                    >
                      <div className="text-sm font-semibold text-slate-900">{s.nom}</div>
                      <div className="text-xs text-slate-500">{s.adresse}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <div className="bk-label">Description</div>
              <Textarea
                data-testid="description-textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                placeholder="Ce que vous constatez, depuis quand, ce que vous avez déjà tenté…"
              />
            </div>

            <VoiceRecorder transcription={transcription} setTranscription={setTranscription} />

            <div className="space-y-2">
              <div className="bk-label">Photos (6 max)</div>
              <PhotoUpload photos={photos} setPhotos={setPhotos} />
            </div>

            <MediaUpload
              medias={videos}
              onChange={setVideos}
              accept="video/*"
              max={1}
              testid="video-upload"
              label="Vidéo courte (1 max · 60 s · 50 Mo)"
            />

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <div className="bk-label">Marque</div>
                <Input data-testid="marque-input" value={marque} onChange={(e) => setMarque(e.target.value)} placeholder="ex : Hikvision" className="h-11" />
              </div>
              <div className="space-y-2">
                <div className="bk-label">N° de série</div>
                <Input data-testid="serie-input" value={serie} onChange={(e) => setSerie(e.target.value)} placeholder="ex : DS-7616-004512" className="h-11" />
              </div>
            </div>

            <div className="space-y-3">
              <div className="bk-label">Degré d'urgence</div>
              {URGENCES.map((u) => (
                <button
                  key={u.key}
                  data-testid={`urgence-${u.key}`}
                  onClick={() => setUrgence(u.key)}
                  className={`w-full text-left rounded-xl border px-4 py-3 flex gap-3 transition-colors ${urgence === u.key ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-400"}`}
                >
                  <span className="mt-1.5 h-3 w-3 rounded-full shrink-0" style={{ background: u.color }} />
                  <span>
                    <span className="block text-sm font-bold text-slate-900">{u.label}</span>
                    <span className="block text-xs text-slate-600 mt-0.5">{u.definition}</span>
                    <span className="block text-xs font-semibold text-[#005CFF] mt-1">{u.sla}</span>
                  </span>
                </button>
              ))}
            </div>

            <label className="flex items-start gap-3 text-sm text-slate-600 cursor-pointer">
              <Checkbox data-testid="rgpd-checkbox" checked={rgpd} onCheckedChange={(v) => setRgpd(!!v)} className="mt-0.5" />
              J'autorise BKPRO à stocker les photos et l'enregistrement vocal transmis pour le traitement de ce SAV (RGPD).
            </label>

            <Button data-testid="ticket-submit-btn" onClick={() => submit(false)} disabled={busy} className="w-full h-14 rounded-xl bg-[#005CFF] hover:bg-[#0047CC] text-base">
              {busy ? <Loader2 className="animate-spin" size={18} /> : "Envoyer le ticket"}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
